import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Trash2, Plus } from 'lucide-react';
import { useStore } from '@/lib/store';
import { UserCombobox } from '@/components/common/EntityCombobox';
import { useUIConfig } from '@/config/ui-config';

export interface BillingRateEntry {
  user: number;
  amount: number;
}

interface BillingRatesFormProps {
  rates: BillingRateEntry[];
  onChange: (rates: BillingRateEntry[]) => void;
  firmId?: string;
  className?: string;
}

export function BillingRatesForm({ 
  rates, 
  onChange, 
  firmId,
  className = '' 
}: BillingRatesFormProps) {
  const { users } = useStore();
  const { form } = useUIConfig(firmId);

  const addBillingRate = () => {
    onChange([...rates, { user: 0, amount: 0 }]);
  };

  const updateRate = (index: number, field: keyof BillingRateEntry, value: number) => {
    const newRates = [...rates];
    newRates[index] = { ...newRates[index], [field]: value };
    onChange(newRates);
  };

  const removeRate = (index: number) => {
    const newRates = rates.filter((_, idx) => idx !== index);
    onChange(newRates);
  };

  const getAvailableUsers = (currentIndex: number) => {
    const usedUserIds = rates
      .filter((_, index) => index !== currentIndex)
      .map(rate => rate.user)
      .filter(userId => userId !== 0);
    
    return users?.filter(user => !usedUserIds.includes(user.id)) || [];
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <Label className={`text-base ${form.labelFontWeight}`}>
        Custom Billing Rates
      </Label>
      
      <div className="text-sm text-muted-foreground">
        Override default hourly rates for specific users on this matter.
      </div>

      {rates.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Hourly Rate</TableHead>
              <TableHead className="w-[50px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.map((rate, index) => {
              const availableUsers = getAvailableUsers(index);
              const currentUser = users?.find(u => u.id === rate.user);
              // Filter out current user from available list to avoid duplicate entries
              const filteredAvailable = availableUsers.filter(u => u.id !== rate.user);
              
              return (
                <TableRow key={index}>
                  <TableCell>
                    <UserCombobox
                      value={rate.user || null}
                      onChange={(id) => id && updateRate(index, 'user', id)}
                      users={currentUser ? [currentUser, ...filteredAvailable] : filteredAvailable}
                      placeholder="Select user"
                    />
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-muted-foreground">€</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={rate.amount || ''}
                        onChange={(e) => updateRate(index, 'amount', Number(e.target.value) || 0)}
                        className="w-[120px]"
                      />
                      <span className="text-sm text-muted-foreground">/hr</span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => removeRate(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove rate</span>
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={addBillingRate}
        className="w-full"
        disabled={!users || users.length === 0 || rates.length >= users.length}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Custom Billing Rate
      </Button>
      
      {rates.length === 0 && (
        <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
          No custom billing rates set. Default rates from user profiles will be used.
        </div>
      )}
    </div>
  );
}
